import dayjs from 'dayjs';
import type { StUserMessageStatsResult } from '../../../services/socialTradingService';
import {
  MESSAGE_STATS_DEFAULT_DAYS,
  MESSAGE_STATS_DEFAULT_TIME_ZONE,
  MESSAGE_STATS_MAX_DAYS,
} from './constants';

/**
 * Today's date in an IANA zone, as `YYYY-MM-DD`.
 *
 * `en-CA` formats exactly that way. Built from Intl rather than a dayjs
 * timezone plugin, which this app does not load. An unusable zone falls back to
 * UTC instead of throwing — the query is what should report a bad zone.
 */
export function todayInZone(timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return dayjs().utc().format('YYYY-MM-DD');
  }
}

/** The default window: the last 7 days, inclusive of today, in the default zone. */
export function defaultMessageStatsRange(timeZone = MESSAGE_STATS_DEFAULT_TIME_ZONE): {
  startDate: string;
  endDate: string;
} {
  const endDate = todayInZone(timeZone);
  return {
    startDate: dayjs(endDate)
      .subtract(MESSAGE_STATS_DEFAULT_DAYS - 1, 'day')
      .format('YYYY-MM-DD'),
    endDate,
  };
}

/** Inclusive day count, so a single day is 1 and the 31-day cap is reachable. */
export const rangeDays = (startDate: string, endDate: string): number =>
  dayjs(endDate).diff(dayjs(startDate), 'day') + 1;

export type MessageStatsRangeError =
  | 'datesRequired'
  | 'timeZoneRequired'
  | 'endBeforeStart'
  | 'rangeTooLong'
  | 'endInFuture';

/**
 * Why the range cannot be queried, or undefined.
 *
 * Every case here is also enforced upstream; checking locally keeps a doomed
 * request — and the error toast it produces — off the screen.
 */
export function validateMessageStatsRange(query: {
  startDate: string;
  endDate: string;
  timeZone: string;
}): MessageStatsRangeError | undefined {
  const { startDate, endDate, timeZone } = query;
  if (!startDate || !endDate) return 'datesRequired';
  if (!timeZone) return 'timeZoneRequired';
  if (dayjs(endDate).isBefore(dayjs(startDate))) return 'endBeforeStart';
  if (rangeDays(startDate, endDate) > MESSAGE_STATS_MAX_DAYS) return 'rangeTooLong';
  if (endDate > todayInZone(timeZone)) return 'endInFuture';
  return undefined;
}

/**
 * The translation key for a failed query. Only the 31-day cap gets its own
 * copy: the other codes are guard rails the UI already prevents, so they share
 * the generic message rather than leaking upstream jargon.
 */
export function messageStatsErrorKey(code?: string): string {
  return code === 'MESSAGE_STATS_RANGE_TOO_LONG'
    ? 'module.socialTrading.messageStatsErrors.rangeTooLong'
    : 'module.socialTrading.messageStatsErrors.generic';
}

export type MessageStatsEmptyState = 'none' | 'noMessagesInRange' | 'noDataYet';

/**
 * Which empty state a result represents.
 *
 * `dataAsOf` is null only when the log has never recorded a message, which is a
 * fresh deploy rather than a quiet week — and the log is not backfilled, so a
 * range before the feature shipped is legitimately empty, never an error.
 */
export function messageStatsEmptyState(
  result: StUserMessageStatsResult | null,
): MessageStatsEmptyState {
  if (!result || result.rows.length > 0) return 'none';
  return result.dataAsOf ? 'noMessagesInRange' : 'noDataYet';
}

/**
 * Splits CSV text into rows of cells.
 *
 * Hand-rolled because the export is small and the alternative is another
 * dependency: it handles quoted fields, embedded commas and newlines, and the
 * doubled `""` escape, which is all the exporter produces.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  const endCell = () => {
    row.push(cell);
    cell = '';
  };
  const endRow = () => {
    endCell();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      endCell();
    } else if (ch === '\n') {
      endRow();
    } else if (ch !== '\r') {
      cell += ch;
    }
  }

  // A trailing newline ends the last row; anything else leaves one to flush.
  if (cell !== '' || row.length > 0) endRow();

  return rows;
}

/**
 * Undoes the exporter's formula-injection guard.
 *
 * The CSV prefixes a cell with `'` when its value starts with `=`, `+`, `-`,
 * `@`, tab or CR so a spreadsheet reads it as text. A real xlsx cell holds a
 * string natively, so the guard is dropped again — otherwise every negative
 * number would arrive with a stray apostrophe.
 */
export function unguardCsvCell(value: string): string {
  return /^'[=+\-@\t\r]/.test(value) ? value.slice(1) : value;
}
